import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { api } from "../../api";
import { useLanguage } from "../../contexts/LanguageContext";
import { settingsLabels } from "./labels";
import { navigate } from "../../lib/router";
import { toast } from "sonner";
import { ChevronLeft, Info, Copy, FileCode } from "lucide-react";

const DEFAULT_TEMPLATE = `CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE FISIOTERAPIA E PILATES

CONTRATADA: {{nome_empresa}}, inscrita no CNPJ sob o nº {{cnpj_empresa}}.

CONTRATANTE: {{nome_cliente}}, inscrito no CPF sob o nº {{cpf_cliente}}, residente e domiciliado em {{endereco_cliente}}.

1. OBJETO
O presente contrato tem como objeto a prestação de serviços de {{nome_plano}} pela CONTRATADA ao CONTRATANTE.

2. VALORES E PAGAMENTO
Pela prestação dos serviços, o CONTRATANTE pagará à CONTRATADA o valor fixo de {{valor_plano}}, com vencimento todo dia {{dia_pagamento}} de cada mês.
Parágrafo único: O atraso no pagamento acarretará multa de 2% e juros de 1% ao mês.

3. REPOSIÇÕES E FALTAS
3.1. As faltas deverão ser comunicadas com antecedência mínima de 24 (vinte e quatro) horas.
3.2. Faltas comunicadas dentro do prazo poderão ser repostas conforme disponibilidade da agenda da CONTRATADA.
3.3. O não comparecimento sem aviso prévio ou aviso fora do prazo implicará na perda da aula/sessão, não dando direito a reposição ou abatimento no valor.

4. VIGÊNCIA E RESCISÃO
Este contrato tem vigência mensal e renovação automática. Caso deseje cancelar, o CONTRATANTE deverá comunicar com 30 dias de antecedência.

Por estar de pleno acordo, as partes firmam o presente.

Local e Data: {{data_hoje}}

__________________________________________
{{nome_empresa}}

__________________________________________
{{nome_cliente}}`;

export default function ContractTemplatePage() {
  const { lang } = useLanguage();
  const t = settingsLabels[lang].contract;
  const [template, setTemplate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await api.getCompany();
      if (res.ok) {
        setTemplate(res.data.contract_template || DEFAULT_TEMPLATE);
      }
      setLoading(false);
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    const res = await api.updateCompany({ contract_template: template });
    if (res.ok) {
      toast.success(t.success);
    } else {
      toast.error(lang === 'pt' ? "Erro ao salvar" : "Error saving");
    }
    setSaving(false);
  };

  const variables = [
    { tag: "{{nome_cliente}}", label: lang === 'pt' ? "Nome do Cliente" : "Client Name" },
    { tag: "{{cpf_cliente}}", label: lang === 'pt' ? "CPF do Cliente" : "Client CPF" },
    { tag: "{{rg_cliente}}", label: lang === 'pt' ? "RG do Cliente" : "Client RG" },
    { tag: "{{endereco_cliente}}", label: lang === 'pt' ? "Endereço do Cliente" : "Client Address" },
    { tag: "{{nome_plano}}", label: lang === 'pt' ? "Nome do Plano" : "Plan Name" },
    { tag: "{{valor_plano}}", label: lang === 'pt' ? "Valor do Plano" : "Plan Value" },
    { tag: "{{dia_pagamento}}", label: lang === 'pt' ? "Dia de Vencimento" : "Due Day" },
    { tag: "{{nome_empresa}}", label: lang === 'pt' ? "Nome da Empresa" : "Company Name" },
    { tag: "{{cnpj_empresa}}", label: lang === 'pt' ? "CNPJ da Empresa" : "Company CNPJ" },
    { tag: "{{data_hoje}}", label: lang === 'pt' ? "Data de Hoje" : "Today's Date" },
  ];

  const insertVariable = (tag: string) => {
    const textarea = document.getElementById("contract-textarea") as HTMLTextAreaElement;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = template;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    
    setTemplate(before + tag + after);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + tag.length, start + tag.length);
    }, 0);
  };

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-5xl p-6 flex flex-col items-center justify-center py-20 gap-4">
        <div className="h-10 w-10 border-4 border-emerald-100 border-t-emerald-600 rounded-full animate-spin" />
        <p className="text-xs font-bold text-neutral-400 uppercase tracking-widest">
          {lang === 'pt' ? 'Carregando Template...' : 'Loading Template...'}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl p-4 md:p-6 space-y-8 animate-in fade-in duration-500">
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate("/app/settings")}
            className="rounded-xl hover:bg-neutral-100 transition-all active:scale-95"
          >
            <ChevronLeft className="h-6 w-6 text-neutral-600" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold text-neutral-800 tracking-tight">{t.pageTitle}</h1>
            <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest leading-none">
              {lang === 'pt' ? 'Configure o modelo de contrato padrão da clínica' : 'Configure the default company contract template'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={() => {
              if (confirm(lang === 'pt' ? "Deseja restaurar o template padrão? Isso substituirá seu texto atual." : "Reset to default template? This will replace your current text.")) {
                setTemplate(DEFAULT_TEMPLATE);
              }
            }}
            className="h-12 px-6 rounded-2xl border-neutral-100 font-bold uppercase text-[10px] tracking-widest hover:bg-neutral-50 transition-all active:scale-95"
          >
            <FileCode className="h-4 w-4 mr-2" />
            {lang === 'pt' ? "Resetar Padrão" : "Reset Default"}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="h-12 px-8 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-[10px] tracking-widest rounded-2xl transition-all active:scale-95"
          >
            {saving ? (
              <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : t.saveButton}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2">
          <div className="rounded-[2.5rem] border border-neutral-100 bg-white shadow-sm overflow-hidden animate-in slide-in-from-bottom-4 duration-700">
            <div className="bg-neutral-50/50 px-8 py-6 border-b border-neutral-50 flex items-center justify-between">
              <div className="flex items-center gap-3 text-xs font-bold text-neutral-500 uppercase tracking-widest">
                <div className="w-8 h-8 rounded-xl bg-white border border-neutral-100 flex items-center justify-center">
                  <FileCode className="h-4 w-4 text-emerald-600" />
                </div>
                {lang === 'pt' ? 'Corpo do Contrato' : 'Contract Body'}
              </div>
              <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-widest">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Editando
              </div>
            </div>
            <textarea
              id="contract-textarea"
              className="min-h-[700px] w-full p-10 text-sm text-neutral-800 focus:outline-none resize-none leading-relaxed font-medium bg-neutral-50/10"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              placeholder={lang === 'pt' ? "Escreva aqui o texto do seu contrato..." : "Write your contract text here..."}
            />
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6 sticky top-6">
          <div className="rounded-[2.5rem] border border-neutral-100 bg-white p-8 shadow-sm animate-in slide-in-from-right-4 duration-700">
            <h2 className="mb-6 flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-neutral-800">
              <div className="w-8 h-8 rounded-xl bg-neutral-50 flex items-center justify-center text-neutral-400">
                <Copy className="h-4 w-4" />
              </div>
              {t.variablesTitle}
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {variables.map((v) => (
                <button
                  key={v.tag}
                  type="button"
                  onClick={() => insertVariable(v.tag)}
                  className="flex flex-col items-start rounded-2xl border-2 border-neutral-50 bg-neutral-50/30 px-5 py-3 text-left transition-all hover:border-emerald-500/30 hover:bg-emerald-50/50 group active:scale-95"
                >
                  <code className="text-[11px] font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors">
                    {v.tag}
                  </code>
                  <span className="text-[9px] text-neutral-400 uppercase font-bold mt-1.5 tracking-wider">
                    {v.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-[2.5rem] bg-blue-50/50 backdrop-blur-sm border border-blue-100 p-8 animate-in slide-in-from-right-4 duration-1000">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-2xl bg-white border border-blue-100 flex items-center justify-center shrink-0 shadow-sm shadow-blue-200/50">
                <Info className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-[11px] text-blue-700/80 leading-relaxed font-bold uppercase tracking-wider">
                {lang === 'pt' 
                  ? "Dica: Clique em uma variável para inseri-la no texto do contrato. Na hora de gerar o documento, os campos serão preenchidos automaticamente."
                  : "Tip: Click on a variable to insert it. When generating the document, fields will be filled automatically."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
